import React, { useState } from 'react'

import { Button, Icon, Select } from '@fourthwall/components'
import type { SelectOption } from '@fourthwall/components/dist/inputs/Select/types'
import { RefreshIcon } from '@fourthwall/icons'

import * as S from './KlaviyoSetLists.styled'

type KlaviyoList = {
  listId: string
  listName: string
}

type KlaviyoSetListsProps = {
  setLists: (requestBody: { membersListId?: string; newsletterListId?: string }) => Promise<void>
  isSettingLists: boolean
  lists: KlaviyoList[]
  refreshLists: () => Promise<void>
  isRefreshing: boolean
  membersListId?: string
  newsletterListId?: string
}

export const KlaviyoSetLists = ({
  setLists,
  isSettingLists,
  lists,
  refreshLists,
  isRefreshing,
  membersListId,
  newsletterListId,
}: KlaviyoSetListsProps) => {
  const CREATE_NEW_LIST_VALUE = 'create-new'
  const [selectedMembersListId, setSelectedMembersListId] = useState<string>(
    membersListId || lists[0]?.listId || CREATE_NEW_LIST_VALUE,
  )
  const [selectedNewsletterListId, setSelectedNewsletterListId] = useState<string>(
    newsletterListId || lists[0]?.listId || CREATE_NEW_LIST_VALUE,
  )

  const handleSetLists = async () => {
    try {
      await setLists({
        membersListId:
          selectedMembersListId === CREATE_NEW_LIST_VALUE ? undefined : selectedMembersListId,
        newsletterListId:
          selectedNewsletterListId === CREATE_NEW_LIST_VALUE ? undefined : selectedNewsletterListId,
      })
    } catch (error) {
      console.error('Failed to set Klaviyo lists:', error)
    }
  }

  const handleRefreshLists = async () => {
    try {
      await refreshLists()
    } catch (error) {
      console.error('Failed to refresh Klaviyo lists:', error)
    }
  }

  const selectOptions: SelectOption[] = [
    ...lists.map((list) => ({
      value: list.listId,
      label: list.listName,
    })),
    { value: CREATE_NEW_LIST_VALUE, label: '+ Create New List' },
  ]

  return (
    <S.Container>
      <S.Title>Configure your Lists</S.Title>
      <S.Description>
        Select which Klaviyo lists to sync your customer data to. Members list will receive your
        supporters. Newsletter list will receive customers who opt-in to marketing emails.
      </S.Description>

      <S.FormSection>
        <S.SelectWithRefreshContainer>
          <Select
            label="Members List"
            value={selectedMembersListId}
            options={selectOptions}
            onValueChange={(value) => setSelectedMembersListId(value)}
            fullWidth
          />
          <Button
            icon={<Icon component={RefreshIcon} width={16} height={16} />}
            onClick={handleRefreshLists}
            loading={isRefreshing}
            disabled={isRefreshing}
          />
        </S.SelectWithRefreshContainer>
      </S.FormSection>

      <S.FormSection>
        <S.SelectWithRefreshContainer>
          <Select
            label="Newsletter List"
            value={selectedNewsletterListId}
            options={selectOptions}
            onValueChange={(value) => setSelectedNewsletterListId(value)}
            fullWidth
          />
          <S.Spacer />
        </S.SelectWithRefreshContainer>
      </S.FormSection>

      <S.ButtonContainer>
        <Button
          label="Save Configuration"
          appearance="primary"
          onClick={handleSetLists}
          loading={isSettingLists}
          disabled={isSettingLists}
        />
      </S.ButtonContainer>
    </S.Container>
  )
}
